import defer from 'lodash/defer';
import {
  initObserveStore, observeStore,
} from '@resy/ui-kitchen';
import getConnectedActions from '../../modules/actions/connectedActions';
import { getResyLocationsSelectedThunk } from '../../../../ui-kitchen/src/reducers/resyLocationsSlice';
import {
  getUserThunk,
  markReservationsChangedThunk,
  setShouldUserReservationsUpdate,
} from '../../../../ui-kitchen/src/reducers/userSlice';
import { selectIsAuthenticated } from '../../../../ui-kitchen/src/reducers/authTokenSlice';
import { getUserReservations, handleReservationCancel, postUserWithAuth } from '../../../../ui-kitchen/src/api/api';
import { reservationsTransform } from '../../../../ui-kitchen/src/api/resyApiTransformer';
import Logger from '../../../../ui-kitchen/src/utils/logger';
import { sanitizeString } from '../../../../ui-kitchen/src/utils/sanitizeString';
import { DATE_TIME_FORMAT } from '../../../../ui-kitchen/src/constants/dateFormats';

angular.module('resyApp')
  .component('resyModals', {
    templateUrl: 'components/modals/modals.html',
    controller: ResyModalsController,
    controllerAs: 'vm',
  });

function ResyModalsController(
  $exceptionHandler,
  $rootScope,
  $route,
  $location,
  $scope,
  Config,
  Analytics
) {
  const vm = this;
  const store = $rootScope.reduxStore;
  const { dispatch, getState } = store;

  vm.closeModal = closeModal;
  vm.clearNotifyParams = clearNotifyParams;
  vm.locationCode = undefined;
  vm.locationSlug = undefined;
  vm.gdaCollectionId = undefined;
  vm.gdaUrl = '/hello';
  vm.hasViewedWelcome = false;
  vm.isCanceled = false;
  vm.isGdaUser = false;
  vm.loadReservations = loadReservations;
  vm.onCloseNotifyModal = onCloseNotifyModal;
  vm.notifyIsOpen = false;
  vm.postToUser = postUserWithAuth;
  vm.cancelToken = null;
  vm.cancelReservation = cancelReservation;
  vm.modal = 'announcement-modal';
  vm.reservation = null;
  vm.errors = null;
  vm.callingCode = null;

  vm.$onInit = init;

  if (selectIsAuthenticated(getState())) {
    dispatch(getUserThunk());
  }

  function init() {
    setupReactors();
    fetchReservation();
  }

  const onMultipleSliceChange = async ({ collections, ipdata, resyConfig }) => {
    const { countryCode } = ipdata;
    const { config } = resyConfig;
    if (config && countryCode) {
      const { country_codes: countries } = config;
      const [country] = countries.filter(
        (value) => (countryCode === value.iso3166)
      );
      vm.callingCode = country.code;
    }
    const { lists } = collections;
    const selected = dispatch(getResyLocationsSelectedThunk());
    if (selected?.code) {
      const key = `${selected.code}-1-${selectIsAuthenticated(getState())}`;
      if (lists.all[key]) {
        const { algorithmic } = lists?.all[key];
        handleCollectionsChange({ algorithmic });
      }
    }
    defer(() => $scope.$apply());
  };

  const unsubscribeMultipleSlices = observeStore(
    store, ({ collections, ipdata, resyConfig }) => ({
      collections, ipdata, resyConfig,
    }), onMultipleSliceChange
  );

  const onVenueReservationsSliceChange = (venueReservationsSlice) => {
    if (venueReservationsSlice.status === 'success') {
      vm.reservations = venueReservationsSlice.data.reservations;
      defer(() => $scope.$apply());
    }
  };
  const observeVenueReservationsSlice = initObserveStore(store, ({ venueReservations }) => (venueReservations));
  const unsubscribeVenueReservationsSlice = observeVenueReservationsSlice(onVenueReservationsSliceChange);

  const onResyLocationsSliceChange = (resyLocations) => {
    const { selected } = resyLocations;
    const locationCode = selected?.code;
    if (vm.locationCode === locationCode) {
      return;
    }
    vm.locationCode = locationCode;
    vm.gdaUrl = constructGdaUrl(vm.locationCode, vm.gdaCollectionId);
    defer(() => $scope.$apply());
  };
  const observeResyLocationsSlice = initObserveStore(store, ({ resyLocations }) => (resyLocations));
  const unsubscribeResyLocationsSlice = observeResyLocationsSlice(onResyLocationsSliceChange);

  let prevUserData = null;
  const onUserSliceChange = (userSlice) => {
    const { userData } = userSlice;

    // Angular 'UserAuth' event handling
    if (prevUserData === null && userData) {
      const { notifyService } = $location.search();
      if (notifyService) {
        setTimeout(() => setModal('notify', { notifyService }), 100);
      }
      setUserGda(userData);
    }

    prevUserData = userData;
    defer(() => $scope.$apply());
  };

  const observeUserSlice = initObserveStore(store, ({ user }) => (user));
  const unsubscribeUserSlice = observeUserSlice(onUserSliceChange);

  function setupReactors() {
    $rootScope.$on(
      'MODAL',
      handleModal
    );

    $scope.$on('$destroy', () => {
      unsubscribeMultipleSlices();
      unsubscribeVenueReservationsSlice();
      unsubscribeResyLocationsSlice();
      unsubscribeUserSlice();
    });
  }

  function loadReservations(venueId, selectedDate, selectedQuantity) {
    const { getVenueReservationsThunk } = getConnectedActions(dispatch);
    if (selectedQuantity > vm.maxQuantity) {
      vm.reservations = [];
    } else {
      const params = {
        venue_id: venueId,
        day: moment(selectedDate).format('YYYY-MM-DD'),
        party_size: selectedQuantity,
      };
      getVenueReservationsThunk({ params });
    }
  }

  async function cancelReservation() {
    const search = $location.search();

    const analyticsPayload = {
      venue: vm.reservation?.venue?.name,
      city: vm.reservation?.venue?.location?.city,
      'is event': !!(vm.reservation?.event && vm.reservation?.event?.name),
      event: (vm.reservation?.event && vm.reservation?.event?.name)
        ? vm.reservation?.event?.name : undefined,
      'lead time': moment(vm.reservation?.day).diff(moment().startOf('day'), 'days'),
      ...(vm.reservation?.day && vm.reservation?.time && {
        time: moment(`${vm.reservation.day} ${vm.reservation.time}`).format(DATE_TIME_FORMAT),
      }),
      'party size': vm.reservation?.seats,
      'time is GDA': !!vm.reservation?.isGda,
      'venue is GDA': vm.reservation?.venue?.isGda,
      'pre-paid': !!(vm.reservation?.payment?.invoice?.total !== 0),
      'cc required': !!(vm.reservation?.cancellation?.fee?.amount !== 0
        || vm.reservation?.payment?.invoice?.total !== 0
        || vm.reservation?.allow_bypass_payment_method !== 1),
      'add-ons': vm.reservation?.hasAddons,
    };

    if (search?.cancelToken) {
      const token = vm.reservation.conciergeResyToken ? {
        concierge_resy_token: vm.reservation.conciergeResyToken,
      } : {
        resy_token: vm.cancelToken,
      };

      await handleReservationCancel(token);

      try {
        vm.isCanceled = true;
        // Concierge cancels change the guest's reservations, not the logged-in user's
        if (!vm.reservation.conciergeResyToken) {
          dispatch(markReservationsChangedThunk());
        }
        dispatch(setShouldUserReservationsUpdate({ type: 'upcoming' }));

        Analytics.event('cancel booking', {
          ...analyticsPayload,
        });
      } catch (err) {
        vm.errors = err;
      }
    } else {
      await handleReservationCancel({ action_token: vm.cancelToken });

      try {
        vm.isCanceled = true;
        if (!search?.conciergeToken) {
          dispatch(markReservationsChangedThunk());
        }
        Analytics.event('cancel booking', {
          ...analyticsPayload,
        });
      } catch (err) {
        vm.errors = err;
      }
    }
  }

  function fetchReservation() {
    resetCancelation();
    const search = $location.search();
    vm.cancelToken = search?.conciergeToken || search?.cancelToken
      || $route?.current?.params?.cancelToken;

    if (!vm.cancelToken) return;
    vm.modal = 'reservation-cancel';
    if (search?.cancelToken) {
      const isConcierge = !!search.conciergeToken;
      const tokenKey = isConcierge ? 'concierge_resy_token' : 'resy_token';
      getUserReservations({ [tokenKey]: vm.cancelToken, book_on_behalf_of: isConcierge }).then(
        (data) => {
          const results = reservationsTransform(data);
          const reservation = results[0];
          vm.reservation = reservation;
          defer(() => $scope.$apply());
        }
      ).catch(
        (err) => {
          vm.errors = err;
          Logger.error('[MODALS] error while fetching user reservation by token', err);
          defer(() => $scope.$apply());
        }
      );
    } else {
      const params = {
        type: 'upcoming',
        action_token: vm.cancelToken,
        resy_token: Config.apiKey,
      };
      getUserReservations(params).then(
        (data) => {
          const results = reservationsTransform(data);
          const reservation = results[0];
          vm.reservation = reservation;

          defer(() => $scope.$apply());
        }
      ).catch(
        (err) => {
          vm.errors = err;
          Logger.error('[MODALS] error while fetching user reservation by action token', err);
          defer(() => $scope.$apply());
        }
      );
    }
  }

  function resetCancelation() {
    vm.reservation = null;
    vm.errors = null;
    vm.cancelToken = null;
    vm.isCanceled = false;
    vm.modal = 'announcement-modal';
  }

  function handleCollectionsChange(data) {
    setGdaCollectionId(data);
  }

  function constructGdaUrl(location = 'new-york-ny', collectionId) {
    if (!collectionId) {
      return `cities/${location}/search`;
    }
    return `cities/${location}/search?list=collection_${collectionId}`;
  }

  function setGdaCollectionId(data) {
    if (vm.gdaCollectionId) {
      return;
    }
    const gda = data.algorithmic && data.algorithmic.find(
      (val) => val.info.type_id === 10
    );

    if (gda) {
      vm.gdaCollectionId = gda.id;
      vm.gdaUrl = constructGdaUrl(vm.locationSlug, vm.gdaCollectionId);
    }
  }

  function closeModal() {
    const search = $location.search();
    if (search.cancelToken) $location.search({ cancelToken: null });

    setModal(null);
  }

  function setUserGda(data) {
    vm.isGdaUser = data.is_global_dining_access;
    vm.hasViewedWelcome = data.viewed_gda_welcome;
  }

  function clearNotifyParams() {
    $location.search('activeNotifications', null).replace();
    $location.search('selectedDate', null).replace();
    $location.search('selectedQuantity', null).replace();
    $location.search('notifyService', null).replace();
    $location.search('venueId', null).replace();
    $location.search('venueName', null).replace();
    $location.search('venueUrl', null).replace();
    $location.search('eventName', null).replace();
    defer(() => $scope.$apply());
  }

  function onCloseNotifyModal() {
    loadReservations(vm.venueId, vm.selectedDate, vm.selectedQuantity);
    $scope.$broadcast('TOGGLE_MODAL');
  }

  function setModal(modalName, data = {}) {
    const callback = () => {
      if (modalName === 'reservation-cancel') {
        fetchReservation();
      }
    };
    vm.modal = modalName;

    if (modalName === 'signin' || modalName === 'signup' || modalName === 'update-phone') {
      vm.successCallback = data.successCallback;
    }

    if (modalName === 'notify') {
      const searchParams = $location.search();
      /**
       * the data needed to populate the notify modal could be coming from the function argument
       * or query params in the case of the user not yet being logged in.
       * Spreading the two objects ensures that the values will be defined
       */
      const notifySearchParams = searchParams.notifyService ? {
        activeNotifications: JSON.parse(searchParams.activeNotifications),
        selectedDate: searchParams.selectedDate,
        selectedQuantity: searchParams.selectedQuantity,
        notifyService: searchParams.notifyService,
        venueId: searchParams.venueId,
        venueName: decodeURIComponent(searchParams.venueName),
        eventName: searchParams.eventName,
      } : {};
      const notifyValues = { ...notifySearchParams, ...data };
      const {
        activeNotifications,
        locationSlug,
        selectedDate,
        selectedQuantity,
        notifyService,
        venueCityCode,
        venueId,
        venueName,
        venueUrl,
        eventName,
      } = notifyValues;

      if (!selectIsAuthenticated(getState())) {
        const encodedNotifyParams = {
          activeNotifications: sanitizeString(JSON.stringify(activeNotifications)),
          locationSlug,
          selectedDate,
          selectedQuantity,
          venueId,
          venueName: encodeURIComponent(venueName),
          venueUrl,
          eventName,
        };

        const searchParamsToSet = {
          ...searchParams,
          ...encodedNotifyParams,
          notifyService,
        };

        $location.search(searchParamsToSet);

        $rootScope.$broadcast('MODAL', 'signin');
      } else {
        loadReservations(venueId, selectedDate, selectedQuantity);
        vm.notifyIsOpen = true;
        vm.activeNotifications = activeNotifications;
        vm.venueCitySlug = locationSlug;
        vm.selectedDate = selectedDate;
        vm.selectedQuantity = selectedQuantity;
        vm.serviceTypeId = notifyService;
        vm.venueCityCode = venueCityCode;
        vm.venueUrl = venueUrl;
        vm.venueId = venueId;
        vm.venueName = venueName;
        vm.eventName = eventName;

        if (!$rootScope.$$phase) {
          defer(() => $scope.$apply());
        } else {
          setTimeout(() => $scope.$digest(), 50);
        }

        $rootScope.$broadcast('TOGGLE_MODAL', notifyService);
      }
    } else if (!$rootScope.$$phase) {
      $scope.$apply(
        callback
      );
    } else {
      try {
        callback();
      } catch (error) {
        $exceptionHandler(error);
      }
    }
  }

  function handleModal(e, modalName, data) {
    setModal(modalName, data);
  }
}

export default () => { };
